import type { ComponentProps, ReactNode } from 'react';
import { Button, cn, Spinner } from '@heroui/react';

/**
 * A button that runs something.
 *
 * ⚠️ THE LABEL DOES NOT CHANGE WHILE IT WORKS, and that is the whole reason
 * this component exists. Every submit in the app used to read
 * `{busy ? 'Сохранение…' : 'Сохранить'}` — nine of them, in four different
 * wordings, one of them nested two deep to also pick between «Добавление…» and
 * «Сохранение…». A label says what the control DOES; a control that renames
 * itself mid-press has stopped saying that, and the reader has to re-read the
 * button to find out whether it is still the one they meant to press. The
 * spinner is the progress report and the label stays put.
 *
 * ⚠️ AND THE WIDTH DOES NOT CHANGE EITHER. The spinner is painted OVER the
 * label rather than beside it: adding a 16px glyph and a gap to a button in a
 * row of buttons shoves every one after it sideways at the exact moment the
 * reader's finger is on the screen. `.button` is already `relative isolate`, so
 * the overlay costs one absolutely-positioned span.
 *
 * ⚠️ `isPending`, NOT `isDisabled`, IS WHAT MARKS THE WAIT. react-aria's Button
 * treats them differently on purpose: `isPending` suppresses press and hover
 * while KEEPING the button focusable, sets `aria-disabled` rather than
 * `disabled`, stops an Enter in a text field re-submitting the form, and
 * announces the state to a screen reader once when it starts and once when it
 * ends. A `disabled` button, by contrast, vanishes from the tab order under the
 * reader's own cursor.
 *
 * ⚠️ WHICH IS WHY `isPending` SUPPRESSES `isDisabled` HERE, AND THAT IS NOT A
 * TIDINESS RULE — it was a real bug, caught by reading the DOM of a button
 * mid-request. Every call site passes `isDisabled={action.isBusy}`, which is
 * right for the nineteen controls that are NOT the one working and wrong for
 * the one that is: `isBusy` is true for it too, so react-aria received both
 * flags and `disabled` won. The rendered button carried `disabled=""` alongside
 * `data-pending="true"` — focus lost, tab order gone, and the assistive
 * announcement `isPending` exists to make fired at an element the reader had
 * just been thrown out of. Suppressing it costs nothing: `isPending` already
 * refuses the press.
 */
export function ActionButton({
  isPending = false,
  isDisabled,
  children,
  className,
  ...rest
}: Omit<ComponentProps<typeof Button>, 'children'> & {
  isPending?: boolean;
  children: ReactNode;
}) {
  return (
    <Button
      {...rest}
      isPending={isPending}
      isDisabled={isPending ? false : isDisabled}
      className={cn('relative', className)}
    >
      {/*
        `visibility: hidden` rather than `display: none`: the label has to keep
        holding the button open at its resting width, and a removed element
        holds nothing. The inner flex reproduces `.button`'s own `gap-2` so a
        label with an icon in it still lays out the same when wrapped.
      */}
      <span className={cn('inline-flex items-center gap-2', isPending && 'invisible')}>
        {children}
      </span>
      {isPending && <ButtonSpinner />}
    </Button>
  );
}

/**
 * The spinner that sits over a working button's label.
 *
 * ⚠️ `aria-hidden` ON THE WRAPPER, and it is not optional. HeroUI's `Spinner`
 * root is a `role="status"` with `aria-label="Loading"` — an English live
 * region, inside a Russian app, announcing the same thing react-aria's
 * `isPending` has already announced properly and in the reader's own language.
 * Hiding the wrapper takes the whole subtree out of the accessibility tree,
 * which is the correct treatment for a purely visual echo of a state that is
 * already exposed.
 *
 * Shared with `IconButton`, whose "label" is a glyph and which hides it the
 * same way.
 */
export function ButtonSpinner() {
  return (
    <span className="absolute inset-0 grid place-items-center" aria-hidden="true">
      <Spinner size="sm" color="current" />
    </span>
  );
}
